import { useState } from 'preact/hooks';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { FormGroup, InputLabel } from '@components/async-form';
import { ModalForm } from '@components/modal-form';
import { DatePicker } from '@components/date-picker';
import { rpx } from 'client/lib/rpx-client';
import type { MeetingSeriesSessionRow } from 'server/dbx/meeting-series';

dayjs.extend(utc);
dayjs.extend(timezone);

type Props = {
  courseId: UUID;
  session: MeetingSeriesSessionRow;
  seriesTimezone: string;
  onChanged: () => void;
};

export function SessionActions({ courseId, session, seriesTimezone, onChanged }: Props) {
  const [open, setOpen] = useState<undefined | 'skip' | 'cancel' | 'reschedule'>(undefined);
  const isDisrupted = !!session.disruption;

  return (
    <>
      <div class="inline-flex items-center gap-2 text-xs">
        {!isDisrupted && (
          <>
            <button
              type="button"
              class="text-gray-600 hover:underline"
              onClick={() => setOpen('reschedule')}
            >
              Reschedule
            </button>
            <span class="text-gray-300">·</span>
            <button
              type="button"
              class="text-gray-600 hover:underline"
              onClick={() => setOpen('skip')}
            >
              Skip
            </button>
            <span class="text-gray-300">·</span>
            <button
              type="button"
              class="text-red-600 hover:underline"
              onClick={() => setOpen('cancel')}
            >
              Cancel
            </button>
          </>
        )}
        {isDisrupted && (
          <button
            type="button"
            class="text-gray-600 hover:underline"
            onClick={async () => {
              await rpx.meetingSeries.restoreSeriesSession({ courseId, sessionId: session.id });
              onChanged();
            }}
          >
            Restore
          </button>
        )}
      </div>
      {open === 'skip' && (
        <DisruptModal
          courseId={courseId}
          sessionId={session.id}
          kind="skipped"
          onClose={() => setOpen(undefined)}
          onDone={() => {
            setOpen(undefined);
            onChanged();
          }}
        />
      )}
      {open === 'cancel' && (
        <DisruptModal
          courseId={courseId}
          sessionId={session.id}
          kind="cancelled"
          onClose={() => setOpen(undefined)}
          onDone={() => {
            setOpen(undefined);
            onChanged();
          }}
        />
      )}
      {open === 'reschedule' && (
        <RescheduleSessionModal
          courseId={courseId}
          session={session}
          seriesTimezone={seriesTimezone}
          onClose={() => setOpen(undefined)}
          onDone={() => {
            setOpen(undefined);
            onChanged();
          }}
        />
      )}
    </>
  );
}

type DisruptProps = {
  courseId: UUID;
  sessionId: UUID;
  kind: 'skipped' | 'cancelled';
  onClose(): void;
  onDone(): void;
};

function DisruptModal({ courseId, sessionId, kind, onClose, onDone }: DisruptProps) {
  const [reason, setReason] = useState('');
  const verb = kind === 'skipped' ? 'Skip' : 'Cancel';
  return (
    <ModalForm
      onClose={onClose}
      title={`${verb} this session?`}
      confirmButtonText={`${verb} session`}
      onSubmit={async () => {
        await rpx.meetingSeries.setSeriesSessionDisruption({
          courseId,
          sessionId,
          disruption: kind,
          reason: reason.trim() || undefined,
        });
        onDone();
      }}
    >
      <p class="mb-3 text-sm text-gray-600">
        {kind === 'skipped'
          ? 'Students keep the recurring series on their calendar; this single date will be marked Skipped and a cancellation will be sent for this occurrence only.'
          : 'This occurrence will be permanently marked Cancelled and a cancellation will be sent for this date only. The rest of the series is unaffected.'}
      </p>
      <FormGroup prop="reason">
        <InputLabel>Reason (optional, shown to students)</InputLabel>
        <input
          type="text"
          class="inline-ruz-input w-full"
          value={reason}
          onChange={(e: any) => setReason(e.target.value)}
          placeholder={kind === 'skipped' ? 'Holiday week' : 'No host available'}
        />
      </FormGroup>
    </ModalForm>
  );
}

type RescheduleProps = {
  courseId: UUID;
  session: MeetingSeriesSessionRow;
  seriesTimezone: string;
  onClose(): void;
  onDone(): void;
};

function RescheduleSessionModal({
  courseId,
  session,
  seriesTimezone,
  onClose,
  onDone,
}: RescheduleProps) {
  const initial = () => {
    const base = session.scheduledAt ?? new Date();
    return wallClockInTzAsLocalDate(base, seriesTimezone);
  };
  const [date, setDate] = useState<Date>(initial);
  return (
    <ModalForm
      onClose={onClose}
      title="Reschedule this session"
      confirmButtonText="Reschedule"
      onSubmit={async () => {
        const newScheduledAt = localWallClockAsTzInstant(date, seriesTimezone);
        await rpx.meetingSeries.rescheduleSession({
          courseId,
          sessionId: session.id,
          newScheduledAt,
        });
        onDone();
      }}
    >
      <p class="mb-3 text-sm text-gray-600">
        Moves this occurrence only. Times are in series timezone:{' '}
        <span class="font-medium">{seriesTimezone}</span>. Calendar invites for everyone are updated
        for this date.
      </p>
      <FormGroup prop="scheduledAt">
        <div class="inline-flex">
          <DatePicker value={date} onChange={(d) => d && setDate(d)} />
        </div>
      </FormGroup>
    </ModalForm>
  );
}

function wallClockInTzAsLocalDate(d: Date, tz: string): Date {
  const wall = dayjs.utc(d).tz(tz).format('YYYY-MM-DDTHH:mm:ss');
  return new Date(wall);
}

function localWallClockAsTzInstant(d: Date, tz: string): Date {
  const wall = dayjs(d).format('YYYY-MM-DDTHH:mm:ss');
  return dayjs.tz(wall, tz).toDate();
}
